// src/hooks/index.js
// Central export for all hooks.
// Import from here rather than individual files where convenient.

// ── Data hooks ────────────────────────────────────────────────────
export { useAlerts }                                        from './useAlerts';
export { useFeed }                                          from './useFeed';

// ── Sync / offline hooks ──────────────────────────────────────────
export { useSync, useSyncWrite }                            from './useSync';
export { useSyncQueue, useDeadLetterQueue,
         useStaleWhileRevalidate, useConnectivity as useOnlineStatus,
         useSWUpdate, useMapTileCache }                     from './offline';

// ── Connectivity hooks ────────────────────────────────────────────
export { useConnectivity, useOfflineBanner,
         useReconnectEffect }                               from './useConnectivity';

// ── GPS hooks ─────────────────────────────────────────────────────
export { useGPS, useWatchGPS, useNearestItems }             from './useGPS';

// ── Push notifications ────────────────────────────────────────────
export { usePushNotifications, PUSH_STATUS }                from './usePushNotifications';

// ── Map ───────────────────────────────────────────────────────────
export { useMap }                                           from './useMap';

// ── General-purpose hooks (kept in index.js — too small for own file) ──────
import { useState, useEffect, useCallback, useRef } from 'react';
import { timeAgo }          from '@services';
import { useFeedStore }    from '@store';

export { useGPS as useGeolocation } from './useGPS';   // alias for backwards compat

export function useMediaCapture() {
  const [media, setMedia] = useState([]);

  const addFile = useCallback((file) => {
    const preview = URL.createObjectURL(file);
    setMedia((prev) => [...prev, { file, preview, type: file.type, name: file.name }]);
  }, []);

  const removeAt = useCallback((index) => {
    setMedia((prev) => {
      URL.revokeObjectURL(prev[index]?.preview);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const clear = useCallback(() => {
    media.forEach((m) => URL.revokeObjectURL(m.preview));
    setMedia([]);
  }, [media]);

  return { media, addFile, removeAt, clear };
}

export function useLocalStorage(key, defaultValue) {
  const [value, setValue] = useState(() => {
    try { return JSON.parse(localStorage.getItem(key) ?? 'null') ?? defaultValue; }
    catch { return defaultValue; }
  });

  const set = useCallback((val) => {
    setValue(val);
    try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
  }, [key]);

  return [value, set];
}

export function useChecklist(items) {
  const [checked, setChecked] = useLocalStorage('na-checklist', {});

  const toggle = useCallback((id) => {
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
  }, [setChecked]);

  const totalItems   = items.reduce((acc, cat) => acc + cat.items.length, 0);
  const totalChecked = Object.values(checked).filter(Boolean).length;
  const pct          = totalItems > 0 ? Math.round((totalChecked / totalItems) * 100) : 0;

  return { checked, toggle, totalItems, totalChecked, pct };
}

export function useAutoRefresh(fn, intervalMs, runOnMount = true) {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => {
    if (runOnMount) fnRef.current();
    const id = setInterval(() => fnRef.current(), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs, runOnMount]);
}

export function useEscalation() {
  const posts = usePostsStore((s) => s.posts);
  return posts.filter((p) => (p.severity ?? p.sev ?? 0) >= 5).length >= 3;
}

export function useTimeAgo(ts) {
  const [label, setLabel] = useState(timeAgo(ts));
  useEffect(() => {
    setLabel(timeAgo(ts));
    const id = setInterval(() => setLabel(timeAgo(ts)), 60_000);
    return () => clearInterval(id);
  }, [ts]);
  return label;
}
export { useIncidents, useIncident, useIncidentActions } from './useIncidents';
export { useNearbyReports, useNearbySOS, useNearbyIncidents, useNearestShelters, useNearestHospitals, useNearestOpenGas, useInferredLocation, useGeoBroadcastArea } from './useNearby';
export { useConnectivityState, NETWORK, BACKEND, SOURCE } from './useConnectivityState';
